import { useHandler } from "@redotech/react-util/hook";
import ChevronRightIcon from "@redotech/redo-icons/chevron-right.svg";
import classNames from "classnames";
import {
  type KeyboardEvent,
  memo,
  type MouseEvent,
  useEffect,
  useRef,
  useState,
} from "react";
import { Dropdown } from "../../dropdown";
import { Flex } from "../../flex";
import { RedoButton, type RedoButtonSize } from "../buttons/redo-button";
import type { RedoCommandMenuItem } from "./redo-command-menu";
import { RedoCommandMenuItemComponent } from "./redo-command-menu-item";
import * as redoCommandMenuCss from "./redo-command-menu.module.css";
import { RedoCommandShortcut } from "./redo-command-shortcut";
import {
  type SubmenuHoverIntent,
  submenuOpenDelayMs,
} from "./submenu-hover-intent";

/** Border + padding above a submenu's first row, backed out so its text sits on the parent's. */
const SUBMENU_CONTENT_INSET_PX = 9;

export const RedoCommandMenuSubmenuItem = memo(
  function RedoCommandMenuSubmenuItem({
    dontRestrainIconWidth,
    item,
    indentForIcon,
    isOpen,
    onOpen,
    onClose,
    closeMenu,
    size = "sm",
    fitToAnchor = false,
    buttonRef: externalRef,
    hoverIntent,
    searchText,
  }: {
    dontRestrainIconWidth?: boolean;
    item: RedoCommandMenuItem;
    indentForIcon: boolean;
    isOpen: boolean;
    onOpen(): void;
    onClose(): void;
    closeMenu(): void;
    size?: RedoButtonSize;
    fitToAnchor?: boolean;
    buttonRef?: (el: HTMLButtonElement | null) => void;
    /** Shared pointer state backing the menu's triangle of safety. */
    hoverIntent?: SubmenuHoverIntent;
    searchText?: string;
  }) {
    const [triggerElement, setTriggerElement] = useState<HTMLDivElement | null>(
      null,
    );
    const submenuDropdownRef = useRef<HTMLDivElement>(null);
    const submenuButtonRefs = useRef<(HTMLButtonElement | null)[]>([]);
    const [submenuFocusedIndex, setSubmenuFocusedIndex] = useState(0);

    const icon = item.Icon || (indentForIcon ? "placeholder" : undefined);

    // Focus first submenu item when submenu opens
    useEffect(() => {
      if (isOpen && item.children) {
        requestAnimationFrame(() => {
          const firstEnabled = item.children?.findIndex((c) => !c.disabled);
          if (firstEnabled !== undefined && firstEnabled >= 0) {
            setSubmenuFocusedIndex(firstEnabled);
            submenuButtonRefs.current[firstEnabled]?.focus();
          }
        });
      }
    }, [isOpen, item.children]);

    const openTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const closeTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

    useEffect(() => {
      if (openTimeoutRef.current) {
        clearTimeout(openTimeoutRef.current);
        openTimeoutRef.current = null;
      }
    }, [searchText]);

    useEffect(() => {
      return () => {
        if (openTimeoutRef.current) {
          clearTimeout(openTimeoutRef.current);
        }
        if (closeTimeoutRef.current) {
          clearTimeout(closeTimeoutRef.current);
        }
      };
    }, []);

    const cancelClose = useHandler(() => {
      if (closeTimeoutRef.current) {
        clearTimeout(closeTimeoutRef.current);
        closeTimeoutRef.current = null;
      }
    });

    const handleMouseEnter = useHandler(() => {
      cancelClose();
      if (!isOpen) {
        // Rows the pointer is only sweeping past on its way to an already-open
        // submenu wait longer, so they don't steal it mid-traverse.
        const delay = submenuOpenDelayMs({
          lastHeadingTowardSubmenuMs:
            hoverIntent?.lastHeadingTowardSubmenuMs ?? null,
          nowMs: Date.now(),
        });
        openTimeoutRef.current = setTimeout(() => onOpen(), delay);
      }
    });

    const handleMouseLeave = useHandler((e: MouseEvent) => {
      if (openTimeoutRef.current) {
        clearTimeout(openTimeoutRef.current);
        openTimeoutRef.current = null;
      }
      // Interactive submenus (e.g. multi-select) stay open on leave; they close
      // when the user dismisses the content or switches to another item.
      if (item.keepSubmenuOpenOnLeave) {
        return;
      }
      const related = e.relatedTarget as Node | null;
      if (
        related &&
        (triggerElement?.contains(related) ||
          submenuDropdownRef.current?.contains(related))
      ) {
        return;
      }
      if (item.customSubmenuContent && isOpen) {
        closeTimeoutRef.current = setTimeout(() => onClose(), 150);
        return;
      }
      onClose();
    });

    const handleTriggerClick = useHandler(
      (event: MouseEvent<HTMLButtonElement>) => {
        event.stopPropagation();
        // An item with both a submenu and its own action commits that action on
        // click; the submenu stays a hover affordance.
        if (item.onClick) {
          closeMenu();
          item.onClick(event);
          return;
        }
        if (isOpen) {
          onClose();
        } else {
          onOpen();
        }
      },
    );

    const handleSubmenuKeyDown = useHandler((e: KeyboardEvent) => {
      if (!item.children) {
        return;
      }

      const enabledIndices = item.children
        .map((child, idx) => (!child.disabled ? idx : -1))
        .filter((idx) => idx !== -1);

      if (enabledIndices.length === 0) {
        return;
      }

      switch (e.key) {
        case "ArrowDown": {
          e.preventDefault();
          e.stopPropagation();
          const currentPos = enabledIndices.indexOf(submenuFocusedIndex);
          const nextPos =
            currentPos === -1 ? 0 : (currentPos + 1) % enabledIndices.length;
          const nextIndex = enabledIndices[nextPos];
          // @ts-expect-error TODO: noUncheckedIndexedAccess FIXME
          setSubmenuFocusedIndex(nextIndex);
          // @ts-expect-error TODO: noUncheckedIndexedAccess FIXME
          submenuButtonRefs.current[nextIndex]?.focus();
          break;
        }
        case "ArrowUp": {
          e.preventDefault();
          e.stopPropagation();
          const currentPos = enabledIndices.indexOf(submenuFocusedIndex);
          const prevPos =
            currentPos === -1
              ? 0
              : (currentPos - 1 + enabledIndices.length) %
                enabledIndices.length;
          const prevIndex = enabledIndices[prevPos];
          // @ts-expect-error TODO: noUncheckedIndexedAccess FIXME
          setSubmenuFocusedIndex(prevIndex);
          // @ts-expect-error TODO: noUncheckedIndexedAccess FIXME
          submenuButtonRefs.current[prevIndex]?.focus();
          break;
        }
        case "ArrowLeft":
        case "Escape": {
          e.preventDefault();
          e.stopPropagation();
          onClose();
          break;
        }
      }
    });

    return (
      <div
        data-arbiter="RedoCommandMenuSubmenuItem"
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        ref={setTriggerElement}
      >
        <Flex
          align="center"
          // Keep the trigger's hover background while its submenu is open, even
          // after the pointer moves onto the submenu content.
          className={classNames(
            redoCommandMenuCss.submenuTriggerRow,
            isOpen && redoCommandMenuCss.submenuTriggerRowOpen,
          )}
        >
          <RedoButton
            buttonId={item.id}
            centerItems={false}
            className={
              fitToAnchor
                ? redoCommandMenuCss.fullWidthDropdownButton
                : redoCommandMenuCss.dropdownButton
            }
            dataTestId={item.dataTestId}
            disabled={item.disabled}
            dontRestrainIconWidth={dontRestrainIconWidth}
            hierarchy="tertiary"
            IconLeading={icon}
            onClick={handleTriggerClick}
            ref={externalRef}
            size={item.size ?? size}
            text={item.text}
            theme={item.theme}
            useOverflowTooltip
          />
          {item.shortcutHotkey && (
            <RedoCommandShortcut hotkey={item.shortcutHotkey} />
          )}
          <ChevronRightIcon className={redoCommandMenuCss.submenuChevron} />
        </Flex>
        {isOpen && (
          <>
            {item.customSubmenuContent && triggerElement ? (
              typeof item.customSubmenuContent === "function" ? (
                item.customSubmenuContent(triggerElement)
              ) : (
                item.customSubmenuContent
              )
            ) : item.children && triggerElement ? (
              <Dropdown
                anchor={triggerElement}
                className={redoCommandMenuCss.submenuDropdown}
                fitToAnchor={false}
                offsetY={-SUBMENU_CONTENT_INSET_PX}
                open
                placement={item.submenuPlacement ?? "right-start"}
                ref={submenuDropdownRef}
              >
                <Flex
                  dir="column"
                  gap="xs"
                  onKeyDown={handleSubmenuKeyDown}
                  role="menu"
                >
                  {item.children.map((child, childIdx) => (
                    <RedoCommandMenuItemComponent
                      dontRestrainIconWidth={dontRestrainIconWidth}
                      fitToAnchor={false}
                      indentForIcon={item.children!.some(
                        (c) => c.Icon !== undefined,
                      )}
                      item={child}
                      itemClicked={closeMenu}
                      key={child.id ?? childIdx}
                      ref={(el) => {
                        submenuButtonRefs.current[childIdx] = el;
                      }}
                      size={size}
                    />
                  ))}
                </Flex>
              </Dropdown>
            ) : null}
          </>
        )}
      </div>
    );
  },
);
